import { useCallback, useEffect, useState } from "react";
import { Loader2, Trash2, AlertTriangle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, money } from "../../lib/api";
import { logError } from "../../lib/log";

const Stat = ({ label, value, testid, tone = "text-amber-200" }) => (
  <div className="hy-glass p-5" data-testid={testid}>
    <div className="hy-label text-[0.55rem]">{label}</div>
    <div className={`font-display text-xl mt-2 ${tone}`}>{value}</div>
  </div>
);

export const CleanupTab = ({ onCleaned }) => {
  const [preview, setPreview] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setPreview(null);
    try {
      const { data } = await api.get("/admin/cleanup/preview");
      setPreview(data);
    } catch (error) {
      logError("Cleanup preview failed", error);
      toast.error("Could not load the cleanup preview");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/cleanup", { confirm: true });
      const r = data.removed;
      const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;
      toast.success(`Removed ${plural(r.registries, "registry record")}, ${plural(r.users, "account")} and ${plural(r.promo_codes, "promo code")}`);
      setConfirmText("");
      await load();
      await onCleaned?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Cleanup failed");
    } finally {
      setBusy(false);
    }
  };

  if (!preview) return <Loader2 className="w-6 h-6 animate-spin text-amber-300 mt-12" />;

  const nothing = preview.registries === 0 && preview.users === 0 && preview.promo_codes === 0;

  return (
    <div className="mt-12 space-y-6" data-testid="admin-cleanup">
      <div className="hy-card p-7">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="hy-label">Demo & test data</div>
            <p className="text-xs text-slate-500 mt-3 max-w-2xl leading-relaxed">
              Records created by test runs and demos are matched by their naming conventions
              (TEST/QA/TMP/DEMO names and throwaway email domains). Real customer records are left
              untouched, and the admin account is never removed.
            </p>
          </div>
          <button onClick={load} className="hy-btn-ghost px-5 py-2.5 text-[0.65rem] uppercase flex items-center gap-2"
            data-testid="cleanup-refresh-btn">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mt-7">
          <Stat label="Registry Records" value={preview.registries} testid="cleanup-stat-registries" />
          <Stat label="Of Those Confirmed" value={preview.paid_registries} testid="cleanup-stat-paid" />
          <Stat label="Revenue Removed" value={money(preview.revenue_removed)} tone="text-violet-300" testid="cleanup-stat-revenue" />
          <Stat label="Test Accounts" value={preview.users} testid="cleanup-stat-users" />
          <Stat label="Test Promo Codes" value={preview.promo_codes} testid="cleanup-stat-promos" />
        </div>
        <p className="text-[0.7rem] text-slate-500 mt-5">{preview.note}</p>
      </div>

      {!nothing && (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="hy-card p-6 lg:col-span-2 overflow-x-auto" data-testid="cleanup-sample-registries">
            <div className="hy-label mb-5">Sample records to remove</div>
            <table className="w-full text-sm">
              <tbody>
                {preview.sample_registries.map((r) => (
                  <tr key={r.registry_id} className="border-b border-amber-500/8">
                    <td className="py-3 pr-4 font-mono-hy text-xs text-amber-200">#{String(r.registry_number).padStart(3, "0")}</td>
                    <td className="py-3 pr-4 text-slate-200">{r.dedication_name}</td>
                    <td className="py-3 pr-4 text-xs text-slate-500">{r.customer_email}</td>
                    <td className={`py-3 pr-4 text-xs ${r.payment_status === "PAID" ? "text-emerald-400" : "text-amber-400"}`}>{r.payment_status}</td>
                    <td className="py-3 text-slate-400 text-xs">{money(r.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {preview.registries > preview.sample_registries.length && (
              <p className="text-xs text-slate-500 mt-4">
                + {preview.registries - preview.sample_registries.length} more
              </p>
            )}
          </div>

          <div className="hy-card p-6 space-y-6">
            <div data-testid="cleanup-sample-users">
              <div className="hy-label mb-4">Accounts</div>
              <div className="space-y-1 text-xs text-slate-400">
                {preview.sample_users.map((u) => <div key={u.email} className="break-all">{u.email}</div>)}
              </div>
            </div>
            <div data-testid="cleanup-sample-promos">
              <div className="hy-label mb-4">Promo codes</div>
              <div className="flex flex-wrap gap-2">
                {preview.sample_promos.map((c) => (
                  <span key={c} className="font-mono-hy text-[0.65rem] text-amber-200/80 border border-amber-500/20 px-2 py-1">{c}</span>
                ))}
                {preview.sample_promos.length === 0 && <span className="text-xs text-slate-500">None</span>}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="hy-card p-7 border-red-500/25" data-testid="cleanup-danger-zone">
        <div className="flex gap-4">
          <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-white text-sm">This cannot be undone</div>
            <p className="text-xs text-slate-500 mt-2 max-w-2xl leading-relaxed">
              Matching registry records, their orders, the test accounts and TEST/QA/TMP/DEMO promo codes are
              permanently deleted. Registry numbers already issued stay retired, so the next number does not move back.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-6 sm:items-center">
              <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
                className="hy-input sm:max-w-xs" placeholder='Type DELETE to confirm (exact case)'
                data-testid="cleanup-confirm-input" />
              <button onClick={run} disabled={busy || nothing || confirmText !== "DELETE"}
                className="hy-btn px-7 py-3.5 text-xs uppercase flex items-center justify-center gap-2 disabled:opacity-40"
                data-testid="cleanup-run-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                {nothing ? "Nothing to clean" : "Remove demo data"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
